import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import ForgotPassword from './ForgotPassword';
import { GoogleIcon, MicrosoftIcon, SitemarkIcon } from './CustomIcons';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function SignInCard() {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [showResetPassword, setShowResetPassword] = React.useState(false);
  const [resetToken, setResetToken] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [resetError, setResetError] = React.useState('');
  const [loginError, setLoginError] = React.useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = React.useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = React.useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setForgotPasswordSuccess(false);
  };

  const handleForgotPassword = async (email: string) => {
    try {
      const response = await fetch('https://api.vivachain.ai/v1/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setForgotPasswordSuccess(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send reset email');
      }
    } catch (err) {
      console.error('Error requesting password reset:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setResetError(errorMessage);
    }
  };

  const handleResetPassword = async () => {
    setResetError('');
    setResetPasswordSuccess(false);

    if (!newPassword || !confirmPassword) {
      setResetError('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters long');
      return;
    }

    try {
      const response = await fetch('https://api.vivachain.ai/v1/auth/reset-password/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: resetToken,
          newPassword,
        }),
      });

      if (response.ok) {
        setResetPasswordSuccess(true);
        setTimeout(() => {
          setShowResetPassword(false);
          setNewPassword('');
          setConfirmPassword('');
        }, 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset password');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password. Please try again.';
      setResetError(errorMessage);
    }
  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const callLoginApi = async (email: string, password: string, provider?: string) => {
    try {
      if (provider) {
        const redirectUrl = encodeURIComponent("https://chat.vivachain.ai");
        window.location.href = `https://api.vivachain.ai/v1/auth/login?provider=${provider}&redirectUrl=${redirectUrl}`;
      } else {
        const response = await fetch(`https://api.vivachain.ai/v1/auth/login?email=${email}&password=${password}`, {
          credentials: "include"
        });

        if (response.ok) {
          window.location.href = 'https://chat.vivachain.ai';
        } else {
          setLoginError('Invalid email or password');
          setEmailError(true);
          setPasswordError(true);
          throw new Error('Login failed');
        }
      }
    } catch (err) {
      console.error('Error logging in:', err);
      setLoginError('Invalid email or password');
      setEmailError(true);
      setPasswordError(true);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginError('');
    if (!validateInputs()) return;
  
    const email = (document.getElementById('email') as HTMLInputElement).value;
    const password = (document.getElementById('password') as HTMLInputElement).value;
  
    callLoginApi(email, password);
  };
  
  const handleProviderLogin = (provider: string) => {
    callLoginApi('', '', provider);
  };

  // Check URL for reset token on component mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('code');
    if (token) {
      setResetToken(token);
      setShowResetPassword(true);
    }
  }, []);

  if (showResetPassword) {
    return (
      <Card variant="outlined">
        <Typography component="h1" variant="h4">
          Reset Password
        </Typography>
        {resetPasswordSuccess ? (
          <Typography color="success.main" sx={{ textAlign: 'center' }}>
            Password reset successful! Redirecting...
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel htmlFor="new-password">New Password</FormLabel>
              <TextField
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setResetError('');
                }}
                error={!!resetError}
                fullWidth
              />
            </FormControl>
            <FormControl>
              <FormLabel htmlFor="confirm-password">Confirm Password</FormLabel>
              <TextField
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setResetError('');
                }}
                error={!!resetError}
                helperText={resetError}
                fullWidth
              />
            </FormControl>
            <Button variant="contained" onClick={handleResetPassword} fullWidth>
              Reset Password
            </Button>
          </Box>
        )}
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <SitemarkIcon />
      </Box>
      <Typography
        component="h1"
        variant="h4"
        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
      >
        Sign in
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
      >
        {loginError && (
          <Typography color="error" sx={{ textAlign: 'center' }}>
            {loginError}
          </Typography>
        )}
        <FormControl>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextField
            error={emailError}
            helperText={emailErrorMessage}
            id="email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={emailError ? 'error' : 'primary'}
            onChange={() => {
              setEmailError(false);
              setLoginError('');
            }}
          />
        </FormControl>
        <FormControl>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <FormLabel htmlFor="password">Password</FormLabel>
            <Link
              component="button"
              type="button"
              onClick={handleClickOpen}
              variant="body2"
              sx={{ alignSelf: 'baseline' }}
            >
              Forgot your password?
            </Link>
          </Box>
          <TextField
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type="password"
            id="password"
            autoComplete="current-password"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
            onChange={() => {
              setPasswordError(false);
              setLoginError('');
            }}
          />
        </FormControl>
        <FormControlLabel
          control={<Checkbox value="remember" color="primary" />}
          label="Remember me"
        />
        <ForgotPassword 
          open={open} 
          handleClose={handleClose}
          onSubmit={handleForgotPassword}
          success={forgotPasswordSuccess}
          error={resetError}
        />
        <Button type="submit" fullWidth variant="contained">
          Sign in
        </Button>
        <Typography sx={{ textAlign: 'center' }}>
          Don&apos;t have an account?{' '}
          <span>
            <Link
              href="/auth/register"
              variant="body2"
              sx={{ alignSelf: 'center' }}
            >
              Sign up
            </Link>
          </span>
        </Typography>
      </Box>
      <Divider>or</Divider>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => handleProviderLogin('google')}
          startIcon={<GoogleIcon />}
        >
          Sign in with Google
        </Button>
      </Box>
    </Card>
  );
}